import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import Chart from 'chart.js/auto';
import axiosInstance from '../api/axiosInstance';

function Analytics() {
  const { isPlusActive, isLoading } = useSubscription();

  // ─────────────────────────────────────────────
  // Chart refs
  // ─────────────────────────────────────────────
  const incomeExpenseRef = useRef(null);
  const cashflowRef = useRef(null);
  const occupancyRef = useRef(null);
  const yieldRef = useRef(null);

  const incomeExpenseChart = useRef(null);
  const cashflowChart = useRef(null);
  const occupancyChart = useRef(null);
  const yieldChart = useRef(null);

  // ─────────────────────────────────────────────
  // Analytics state
  // ─────────────────────────────────────────────
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(null);

  const [overview, setOverview] = useState({
    totalProperties: 0,
    totalTenants: 0,
    occupancyRate: 0,
    monthlyRevenue: 0,
  });

  const [incomeExpenses, setIncomeExpenses] = useState({
    labels: [],
    income: [],
    expenses: [],
  });

  const [cashflow, setCashflow] = useState({
    labels: [],
    data: [],
  });

  const [occupancy, setOccupancy] = useState({
    occupied: 0,
    vacant: 0,
  });

  const [rentalYield, setRentalYield] = useState({
    labels: [],
    data: [],
  });

  const [topProperties, setTopProperties] = useState([]);
  const [leaseExpirations, setLeaseExpirations] = useState([]);

  // ─────────────────────────────────────────────
  // Fetch analytics data
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (isLoading || !isPlusActive) return;

    const fetchAnalytics = async () => {
      try {
        setLoadingAnalytics(true);

        const res = await axiosInstance.get('/api/analytics');
        const data = res?.data ?? {};

        // ─────────────────────────────
        // Overview
        // ─────────────────────────────
        setOverview({
          totalProperties: data.totalProperties ?? 0,
          totalTenants: data.totalTenants ?? 0,
          occupancyRate: data.occupancyRate ?? 0,
          monthlyRevenue:
            data.monthlyIncome ??
            data.monthlyRevenue ??
            data.receivedGBP ??
            0,
        });

        // ─────────────────────────────
        // Income vs Expenses
        // ─────────────────────────────
        setIncomeExpenses({
          labels: data.financials?.labels ?? [],
          income: data.financials?.income ?? [],
          expenses: data.financials?.expenses ?? [],
        });

        setCashflow({
          labels: data.financials?.labels ?? [],
          data: data.financials?.cashflow ?? [],
        });

        // ─────────────────────────────
        // Occupancy
        // ─────────────────────────────
        setOccupancy({
          occupied:
            data.occupancy?.occupied ??
            data.totalTenants ??
            0,
          vacant:
            data.occupancy?.vacant ??
            ((data.totalProperties ?? 0) - (data.totalTenants ?? 0)),
        });

        // ─────────────────────────────
        // Rental Yield / Top props / Leases
        // ─────────────────────────────
        setRentalYield({
          labels: data.rentalYield?.map(r => r.property) ?? [],
          data: data.rentalYield?.map(r => r.yield) ?? [],
        });

        setTopProperties(data.topProperties ?? []);
        setLeaseExpirations(data.upcomingLeases ?? []);
      } catch (err) {
        console.error(err);
        setAnalyticsError('Failed to load analytics');
      } finally {
        setLoadingAnalytics(false);
      }
    };

    fetchAnalytics();
  }, [isPlusActive, isLoading]);

  // ─────────────────────────────────────────────
  // Charts
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (loadingAnalytics || !isPlusActive) {
      incomeExpenseChart.current?.destroy?.();
      cashflowChart.current?.destroy?.();
      occupancyChart.current?.destroy?.();
      yieldChart.current?.destroy?.();
      return;
    }

    // Income vs Expenses Chart
    if (incomeExpenseRef.current) {
      incomeExpenseChart.current?.destroy?.();
      incomeExpenseChart.current = new Chart(
        incomeExpenseRef.current.getContext('2d'),
        {
          type: 'bar',
          data: {
            labels: incomeExpenses.labels,
            datasets: [
              {
                label: 'Actual Income',
                data: incomeExpenses.income,
                backgroundColor: 'rgba(75,192,192,0.7)',
              },
              {
                label: 'Expenses',
                data: incomeExpenses.expenses,
                backgroundColor: 'rgba(255,99,132,0.7)',
              },
            ],
          },
          options: { responsive: true, maintainAspectRatio: false },
        }
      );
    }

    // Cashflow Chart
    if (cashflowRef.current) {
      cashflowChart.current?.destroy?.();
      cashflowChart.current = new Chart(
        cashflowRef.current.getContext('2d'),
        {
          type: 'line',
          data: {
            labels: cashflow.labels,
            datasets: [{ label: 'Cashflow', data: cashflow.data, fill: true }],
          },
          options: { responsive: true, maintainAspectRatio: false },
        }
      );
    }

    // Occupancy Chart
    if (occupancyRef.current) {
      occupancyChart.current?.destroy?.();
      occupancyChart.current = new Chart(
        occupancyRef.current.getContext('2d'),
        {
          type: 'doughnut',
          data: {
            labels: ['Occupied', 'Vacant'],
            datasets: [
              {
                data: [
                  occupancy.occupied ?? 0,
                  occupancy.vacant ?? 0,
                ],
                backgroundColor: ['#4BC0C0', '#333'],
              },
            ],
          },
          options: { responsive: true, maintainAspectRatio: false },
        }
      );
    }

    // Rental Yield Chart
    if (yieldRef.current) {
      yieldChart.current?.destroy?.();
      yieldChart.current = new Chart(
        yieldRef.current.getContext('2d'),
        {
          type: 'bar',
          data: {
            labels: rentalYield.labels,
            datasets: [
              { label: 'Rental Yield (%)', data: rentalYield.data },
            ],
          },
          options: { responsive: true, maintainAspectRatio: false },
        }
      );
    }

    // Cleanup
    return () => {
      incomeExpenseChart.current?.destroy?.();
      cashflowChart.current?.destroy?.();
      occupancyChart.current?.destroy?.();
      yieldChart.current?.destroy?.();
    };
  }, [loadingAnalytics, incomeExpenses, cashflow, occupancy, rentalYield, isPlusActive]);

  // ─────────────────────────────────────────────
  // Loading & Paywall states
  // ─────────────────────────────────────────────
  if (isLoading) return <div className="pt-10 text-white text-center">Checking subscription…</div>;

  if (!isPlusActive)
    return (
      <div className="pt-10 md:pt-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl">Analytics</h1>
      </div>
      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

        <div className="fixed inset-0 flex items-center justify-center">
          <div className="bg-black/70 p-8 rounded-2xl text-center">
            <p className="text-xl mb-4">Analytics is a Beem Plus feature</p>
            <Link to="/settings" className="bg-[#AF9B8A] px-4 py-2 rounded-xl">
              Upgrade
            </Link>
          </div>
        </div>
      </div>
    );

  if (loadingAnalytics) return <div className="pt-10 text-white text-center">Loading analytics…</div>;

  if (analyticsError) return <div className="pt-10 text-white text-center">{analyticsError}</div>;

  // ─────────────────────────────────────────────
  // Full analytics UI
  // ─────────────────────────────────────────────
  return (
    <div className="pt-10 md:pt-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl">Analytics</h1>
      </div>
      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

      <div className="grid md:grid-cols-4 gap-6 mb-12">
        <Stat title="Total Properties" value={overview.totalProperties} />
        <Stat title="Total Tenants" value={overview.totalTenants} />
        <Stat title="Occupancy Rate" value={`${overview.occupancyRate}%`} />
        <Stat
          title="Actual Income This Month"
          value={`£${overview.monthlyRevenue.toLocaleString()}`}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-12">
        <ChartBox title="Actual Income vs Expenses" refProp={incomeExpenseRef} />
        <ChartBox title="Cashflow Trend" refProp={cashflowRef} />
        <ChartBox title="Occupancy Rate" refProp={occupancyRef} />
        <ChartBox title="Rental Yield" refProp={yieldRef} />
      </div>

      <h2 className="text-3xl mb-4">Top Performing Properties</h2>
      <div className="h-px w-36 bg-[#AF9B8A] mb-6"></div>
      <table className="w-full text-white mb-12">
        <thead>
          <tr className="text-left text-sm text-white/60">
            <th className="py-2">Property</th>
            <th className="py-2">Actual Income</th>
            <th className="py-2">Actual Yield</th>
            <th className="py-2">Occupancy</th>
          </tr>
        </thead>
        <tbody>
          {topProperties.map(p => (
            <tr key={p.id}>
              <td>{p.name}</td>
              <td>£{Number(p.actualRentIncomeGBP ?? p.rent ?? 0).toLocaleString()}</td>
              <td>{p.yield}%</td>
              <td>{p.occupancy}%</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="text-3xl text-white mb-4">Upcoming Lease Expirations</h2>
      <div className="h-px w-36 bg-[#AF9B8A] mb-6"></div>
      <div className="grid md:grid-cols-2 gap-6">
        {leaseExpirations.map(l => (
          <div key={l.id} className="bg-white/10 p-6 rounded-xl">
            <h3>{l.tenant}</h3>
            <p>{l.property}</p>
            <p>Expiry: {l.expiry}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="bg-white/10 p-6 rounded-xl text-center">
      <h3 className="mb-2">{title}</h3>
      <p className="text-2xl text-[#AF9B8A]">{value}</p>
    </div>
  );
}

function ChartBox({ title, refProp }) {
  return (
    <div className="bg-white/10 p-6 rounded-xl">
      <h3 className="mb-4">{title}</h3>
      <div className="h-64">
        <canvas ref={refProp} />
      </div>
    </div>
  );
}

export default Analytics;
